import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Field, useFormState } from "react-final-form";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { setDateRange } from "../actions/estimate";
import { Error, required } from "./Validation";

const DatePicker = ({ unavailableDays, flexibility, setRange }) => {
  const { values } = useFormState();
  const { move_date, date_flexible } = values;
  const [minDate, setMinDate] = useState(null);
  const [ranges, setRanges] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const min = new Date();
    min.setDate(min.getDate() + unavailableDays);
    setMinDate(min);
  }, [unavailableDays]);

  const calculateRange = useCallback(
    (selectedDate) => {
      if (!selectedDate) {
        return null;
      }
      // shift the range so it doesn't overlap with disabled days
      let offset = Math.abs(selectedDate - minDate);
      offset = Math.ceil(offset / (1000 * 60 * 60 * 24));
      offset = flexibility - offset + 1;
      if (offset < 0) {
        offset = 0;
      }

      const startDate = new Date(selectedDate.getTime());
      startDate.setDate(startDate.getDate() - flexibility + offset);
      const endDate = new Date(selectedDate.getTime());
      endDate.setDate(endDate.getDate() + flexibility + offset);

      return {
        startDate,
        endDate,
      };
    },
    [minDate, flexibility]
  );

  useEffect(() => {
    // shift the range so it doesn't overlap with disabled days
    let offset = Math.abs(move_date - minDate);
    offset = Math.ceil(offset / (1000 * 60 * 60 * 24));
    offset = flexibility - offset + 1;
    if (offset < 0) {
      offset = 0;
    }

    if (move_date) {
      const startDate = new Date(move_date.getTime());
      startDate.setDate(startDate.getDate() - flexibility + offset);
      const endDate = new Date(move_date.getTime());
      endDate.setDate(endDate.getDate() + flexibility + offset);
      setRanges([
        {
          ...calculateRange(move_date),
          color: "#003976",
          disabled: true,
          showDateDisplay: false,
        },
      ]);
    }
  }, [move_date, minDate, calculateRange, flexibility]);

  const handlePreviewChange = (hoveredDate) => {
    if (move_date && date_flexible) {
      setPreview(calculateRange(hoveredDate));
    } else {
      setPreview(null);
    }
  };

  const initialFocusDate = new Date();
  initialFocusDate.setDate(initialFocusDate.getDate() + unavailableDays);

  return (
    <div>
      <Field name="move_date" validate={required("Please select a date")}>
        {({ input }) => (
          <Calendar
            showMonthAndYearPickers={false}
            shownDate={initialFocusDate}
            minDate={minDate}
            onChange={(date) => {
              input.onChange(date);
              setRange(calculateRange(date));
            }}
            onPreviewChange={handlePreviewChange}
            date={typeof input.value === "object" ? input.value : null}
            displayMode={!date_flexible ? "date" : "dateRange"}
            className={!date_flexible ? "date" : "dateRange"}
            ranges={ranges}
            preview={preview}
            color="#ef4044"
          />
        )}
      </Field>
      <Error name="move_date" />
      <label className="checkbox-wrap" htmlFor="date_flexible">
        <Field
          id="date_flexible"
          name="date_flexible"
          component="input"
          type="checkbox"
          className="checkbox"
          parse={(v) => !v}
          format={(v) => !v}
        />{" "}
        I need a specific move date
      </label>
    </div>
  );
};

DatePicker.propTypes = {
  unavailableDays: PropTypes.number,
  flexibility: PropTypes.number,
  setRange: PropTypes.func.isRequired,
};

DatePicker.defaultProps = {
  unavailableDays: 7,
  flexibility: 4,
};

const mapStateToProps = (state) => ({
  unavailableDays: state.settings.unavailable_days,
  flexibility: state.settings.flexibility,
});

const mapDispatchToProps = (dispatch) => ({
  setRange: (range) => dispatch(setDateRange(range)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DatePicker);
